import React, { useState } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';
import Button from '../ui/Button';

interface PropertyField {
  name: string;
  type: string;
  required: boolean;
  unique: boolean;
  indexed: boolean;
  description: string;
}

interface AddNodeDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (nodeData: {
    label: string;
    description: string;
    category?: string;
    properties: PropertyField[];
  }) => void;
}

const PROPERTY_TYPES = [
  'String',
  'Integer',
  'Float',
  'Boolean',
  'DateTime',
  'Date',
  'Time',
  'LocalDateTime',
  'LocalTime',
  'Duration',
  'Point',
  'List<String>',
  'List<Integer>'
];

const NODE_CATEGORIES = [
  'Entity',
  'Event',
  'Dimension',
  'Fact',
  'Reference',
  'Transaction',
  'Other'
];

export const AddNodeDialog: React.FC<AddNodeDialogProps> = ({ isOpen, onClose, onAdd }) => {
  const [label, setLabel] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<string>('Entity');
  const [properties, setProperties] = useState<PropertyField[]>([
    {
      name: '',
      type: 'String',
      required: false,
      unique: false,
      indexed: false,
      description: ''
    }
  ]);

  const [errors, setErrors] = useState<{
    label?: string;
    properties?: string[];
  }>({});

  if (!isOpen) return null;

  const handleAddProperty = () => {
    setProperties([
      ...properties,
      {
        name: '',
        type: 'String',
        required: false,
        unique: false,
        indexed: false,
        description: ''
      }
    ]);
  };

  const handleRemoveProperty = (index: number) => {
    const newProperties = properties.filter((_, i) => i !== index);
    setProperties(newProperties);
  };

  const handlePropertyChange = (index: number, field: keyof PropertyField, value: any) => {
    const newProperties = [...properties];
    newProperties[index] = {
      ...newProperties[index],
      [field]: value
    };
    setProperties(newProperties);
  };

  const validate = (): boolean => {
    const newErrors: typeof errors = {};

    // Validate label
    if (!label.trim()) {
      newErrors.label = 'Node label is required';
    } else if (!/^[A-Z][a-zA-Z0-9_]*$/.test(label)) {
      newErrors.label = 'Label must start with uppercase letter and contain only alphanumeric characters and underscores';
    }

    // Validate properties
    const propertyErrors: string[] = [];
    properties.forEach((prop, index) => {
      if (prop.name.trim()) {
        if (!/^[a-z][a-zA-Z0-9_]*$/.test(prop.name)) {
          propertyErrors[index] = 'Property name must start with lowercase letter';
        }
        // Check for duplicate property names
        const duplicates = properties.filter((p, i) => i !== index && p.name === prop.name);
        if (duplicates.length > 0) {
          propertyErrors[index] = 'Duplicate property name';
        }
      }
    });

    if (propertyErrors.length > 0) {
      newErrors.properties = propertyErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    // Filter out empty properties
    const validProperties = properties.filter(p => p.name.trim() !== '');

    onAdd({
      label: label.trim(),
      description: description.trim(),
      category,
      properties: validProperties
    });

    // Reset form
    setLabel('');
    setDescription('');
    setCategory('Entity');
    setProperties([
      {
        name: '',
        type: 'String',
        required: false,
        unique: false,
        indexed: false,
        description: ''
      }
    ]);
    setErrors({});
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-neutral-200">
          <h2 className="text-xl font-bold text-neutral-900">Add New Node</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-neutral-100 rounded transition-colors"
            aria-label="Close dialog"
          >
            <X className="w-5 h-5 text-neutral-500" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Node Label */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Node Label <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g., Product, Customer, Order"
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.label
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-neutral-300 focus:ring-blue-500'
              }`}
            />
            {errors.label && (
              <p className="mt-1 text-sm text-red-600">{errors.label}</p>
            )}
            <p className="mt-1 text-xs text-neutral-500">
              Must start with uppercase letter (e.g., Product, not product)
            </p>
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Category
            </label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {NODE_CATEGORIES.map((cat) => (
                <option key={cat} value={cat}>
                  {cat}
                </option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe the purpose of this node..."
              rows={3}
              className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Properties */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-neutral-700">
                Properties
              </label>
              <Button
                variant="outline"
                size="sm"
                onClick={handleAddProperty}
              >
                <Plus className="w-4 h-4" />
                Add Property
              </Button>
            </div>

            <div className="space-y-4">
              {properties.map((property, index) => (
                <div
                  key={index}
                  className="p-4 border border-neutral-200 rounded-lg space-y-3 bg-neutral-50"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 grid grid-cols-2 gap-3">
                      {/* Property Name */}
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">
                          Property Name
                        </label>
                        <input
                          type="text"
                          value={property.name}
                          onChange={(e) => handlePropertyChange(index, 'name', e.target.value)}
                          placeholder="e.g., productId, name"
                          className={`w-full px-2 py-1 text-sm border rounded focus:outline-none focus:ring-1 ${
                            errors.properties?.[index]
                              ? 'border-red-300 focus:ring-red-500'
                              : 'border-neutral-300 focus:ring-blue-500'
                          }`}
                        />
                        {errors.properties?.[index] && (
                          <p className="mt-1 text-xs text-red-600">{errors.properties[index]}</p>
                        )}
                      </div>

                      {/* Property Type */}
                      <div>
                        <label className="block text-xs font-medium text-neutral-600 mb-1">
                          Type
                        </label>
                        <select
                          value={property.type}
                          onChange={(e) => handlePropertyChange(index, 'type', e.target.value)}
                          className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          {PROPERTY_TYPES.map((type) => (
                            <option key={type} value={type}>
                              {type}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {/* Remove Button */}
                    {properties.length > 1 && (
                      <button
                        onClick={() => handleRemoveProperty(index)}
                        className="ml-2 p-1 hover:bg-red-100 rounded transition-colors"
                        aria-label="Remove property"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>

                  {/* Property Description */}
                  <div>
                    <label className="block text-xs font-medium text-neutral-600 mb-1">
                      Description
                    </label>
                    <input
                      type="text"
                      value={property.description}
                      onChange={(e) => handlePropertyChange(index, 'description', e.target.value)}
                      placeholder="Describe this property..."
                      className="w-full px-2 py-1 text-sm border border-neutral-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>

                  {/* Property Flags */}
                  <div className="flex items-center gap-4">
                    <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={property.required}
                        onChange={(e) => handlePropertyChange(index, 'required', e.target.checked)}
                        className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      Required
                    </label>

                    <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={property.unique}
                        onChange={(e) => handlePropertyChange(index, 'unique', e.target.checked)}
                        className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      Unique
                    </label>

                    <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={property.indexed}
                        onChange={(e) => handlePropertyChange(index, 'indexed', e.target.checked)}
                        className="rounded border-neutral-300 text-blue-600 focus:ring-blue-500"
                      />
                      Indexed
                    </label>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-neutral-200 bg-neutral-50">
          <Button
            variant="outline"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
          >
            Add Node
          </Button>
        </div>
      </div>
    </div>
  );
};
